import { Globe, Calendar, Users, Wrench, Trophy, MessageCircle } from "lucide-react";
import { z } from "zod";

const BenefitSchema = z.object({
	icon: z.any(),
	title: z.string(),
	description: z.string(),
	color: z.string(),
});

const BenefitsSchema = z.object({
	badgeText: z.string().default("Member Benefits"),
	header: z.string().default("Why Top PropTech Founders"),
	highlightText: z.string().default("Choose Our Community"),
	description: z
		.string()
		.default(
			"Join an exclusive network designed to accelerate your PropTech journey with targeted resources, meaningful connections, and industry-specific opportunities."
		),
	benefits: z.array(BenefitSchema).default([
		{
			icon: Globe,
			title: "Global Visibility",
			description:
				"Get featured on our global founders map and showcase your startup to a worldwide PropTech audience.",
			color: "blue",
		},
		{
			icon: Calendar,
			title: "Curated Events Access",
			description:
				"Priority access to exclusive PropTech events, webinars, and networking sessions with industry leaders.",
			color: "teal",
		},
		{
			icon: Users,
			title: "Peer Networking",
			description:
				"Connect directly with fellow PropTech founders for partnerships, advice, and collaborative opportunities.",
			color: "orange",
		},
		{
			icon: Wrench,
			title: "Beta Tool Access",
			description:
				"First access to new platform features and tools designed specifically for PropTech businesses.",
			color: "green",
		},
		{
			icon: Trophy,
			title: "Industry Recognition",
			description:
				"Participate in founder spotlights, case studies, and thought leadership opportunities.",
			color: "purple",
		},
		{
			icon: MessageCircle,
			title: "Direct Support",
			description:
				"Get personalized support from our team and community moderators to maximize your experience.",
			color: "pink",
		},
	]),
});

type BenefitsProps = z.input<typeof BenefitsSchema>;

const Benefits: React.FC<BenefitsProps> = (props = {}) => {
	const data = BenefitsSchema.parse(props);

	const getColorClasses = (color: string) => {
		const colors = {
			blue: "bg-blue-100 text-blue-800",
			teal: "bg-teal-100 text-teal-800",
			orange: "bg-orange-100 text-orange-800",
			green: "bg-green-100 text-green-800",
			purple: "bg-purple-100 text-purple-800",
			pink: "bg-pink-100 text-pink-800",
		};
		return colors[color as keyof typeof colors] || "bg-gray-100 text-gray-800";
	};

	return (
		<section id="benefits" className="bg-white py-24">
			<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
				<div className="mb-16 text-center">
					<span className="mb-4 inline-flex items-center rounded-full bg-orange-100 px-4 py-2 text-sm font-medium text-orange-800">
						<Trophy className="mr-2 h-4 w-4" />
						{data.badgeText}
					</span>
					<h2 className="mb-6 text-3xl font-bold text-gray-900 md:text-5xl">
						{data.header}
						<span className="block bg-gradient-to-r from-orange-600 to-pink-600 bg-clip-text text-transparent">
							{data.highlightText}
						</span>
					</h2>
					<p className="mx-auto max-w-3xl text-xl leading-relaxed text-gray-600">
						{data.description}
					</p>
				</div>

				<div className="mb-16 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
					{data.benefits.map((benefit, index) => (
						<div
							key={index}
							className="group rounded-2xl border border-transparent bg-gray-50 p-8 transition-all duration-300 hover:-translate-y-2 hover:border-gray-200 hover:bg-white hover:shadow-xl"
						>
							<div
								className={`h-16 w-16 ${getColorClasses(
									benefit.color
								)} mb-6 flex items-center justify-center rounded-2xl transition-transform duration-300 group-hover:scale-110`}
							>
								<benefit.icon className="h-8 w-8" />
							</div>
							<h3 className="mb-4 text-xl font-bold text-gray-900">{benefit.title}</h3>
							<p className="leading-relaxed text-gray-600">{benefit.description}</p>
						</div>
					))}
				</div>
			</div>
		</section>
	);
};

export default Benefits;
